import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const AuthContext = createContext(null);

const API = `${window.location.origin}/api`;

// إعدادات تسجيل الخروج التلقائي - معطلة (جلسة دائمة)
const SESSION_TIMEOUT_MINUTES = 525600; // سنة كاملة = 365 يوم × 24 ساعة × 60 دقيقة
const SESSION_WARNING_MINUTES = 0; // لا تحذير

// جميع الميزات متاحة لكل التراخيص المفعّلة
const ALL_FEATURES = [
  "pos",
  "inventory",
  "customers",
  "suppliers",
  "expenses",
  "reports",
  "employees",
  "live",
  "customer_debts",
  "supplier_debts",
  "damages",
  "cash_register",
  "installments",
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(false);
  
  // مراجع لتتبع النشاط
  const lastActivityRef = useRef(Date.now());
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  // تحديث وقت آخر نشاط
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem("lastActivity", Date.now().toString());
    setSessionWarning(false);
  }, []);

  // التحقق من انتهاء الجلسة - معطل للجلسة الدائمة
  const checkSessionTimeout = useCallback(() => {
    // الجلسة دائمة - لا حاجة للتحقق
    return;
  }, []);

  // إعداد مراقبة النشاط
  useEffect(() => {
    if (!token || !user) return;

    // تتبع النشاط
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach(event => {
      window.addEventListener(event, updateLastActivity);
    });

    // فحص الجلسة كل دقيقة
    const intervalId = setInterval(checkSessionTimeout, 60000);

    // تنظيف
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
      clearInterval(intervalId);
    };
  }, [token, user, updateLastActivity, checkSessionTimeout]);

  // تمديد الجلسة
  const extendSession = useCallback(() => {
    updateLastActivity();
    setSessionWarning(false);
    toast.success("تم تمديد الجلسة");
  }, [updateLastActivity]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    fetchSubscriptionInfo();
  }, [token]);

  useEffect(() => {
    // دائماً RTL للعربية
    document.documentElement.dir = "rtl";
    document.documentElement.lang = "ar";
  }, []);

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      // ✨ Batched: set user AND clear loading together (avoids race in ProtectedRoute)
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
      setLoading(false);
    } catch (error) {
      console.error("Auth error:", error);
      logout();
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/subscription-info`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    }
  };

  const login = async (email, password) => {
    const loginData = { email, password };
    const response = await axios.post(`${API}/auth/login`, loginData);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("lastActivity", Date.now().toString());
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(newUser));
    localStorage.setItem("lastActivity", Date.now().toString());
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("lastActivity");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setSessionWarning(false);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  // حالة الترخيص الجديدة
  const isActive = user?.is_active || false;
  const licenseType = user?.license_type || "none";
  const userRole = user?.role || "";
  const isTrial = licenseType === "trial";
  const trialEndDate = user?.trial_end_date;
  
  // Super Admin و Admin يحصلون على كل الصلاحيات تلقائياً
  const isSuperAdmin = userRole === "super_admin" || userRole === "admin";
  
  // canEdit = true إذا كان super_admin أو لديه ترخيص صالح
  const canEdit = isSuperAdmin || (isActive && (
    licenseType === "full" || 
    licenseType === "lifetime" || 
    licenseType === "whitelabel" || 
    licenseType === "professional" ||
    licenseType === "regular" ||
    licenseType === "trial"
  ));
  
  const isWhiteLabel = licenseType === "whitelabel";
  const allowedFeatures = canEdit ? ALL_FEATURES : [];
  
  // حساب أيام التجربة المتبقية
  const getTrialDaysLeft = () => {
    if (!isTrial || !trialEndDate) return 0;
    const endDate = new Date(trialEndDate);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };
  
  // التحقق من إمكانية الوصول لميزة معينة
  const canAccessFeature = (feature) => {
    if (!canEdit) return false;
    return ALL_FEATURES.includes(feature);
  };
  
  // الحصول على اسم نوع الترخيص بالعربية
  const getLicenseTypeName = () => {
    // Super Admin يحصل على ترخيص دائم تلقائياً
    if (isSuperAdmin) return "ترخيص دائم";
    
    switch (licenseType) {
      case "lifetime": return "ترخيص دائم";
      case "full": return "ترخيص دائم";
      case "whitelabel": return "White Label";
      case "professional": return "ترخيص احترافي";
      case "trial": return `تجربة مجانية (${getTrialDaysLeft()} أيام)`;
      default: return "بانتظار التفعيل";
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    // Theme
    theme,
    toggleTheme,
    // License System
    isActive,
    licenseType,
    canEdit,
    isWhiteLabel,
    isTrial,
    isSuperAdmin,
    trialEndDate,
    getTrialDaysLeft,
    allowedFeatures,
    canAccessFeature,
    getLicenseTypeName,
    subscriptionInfo,
    ALL_FEATURES,
    // Session Management
    sessionWarning,
    extendSession,
    SESSION_TIMEOUT_MINUTES,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
